'use client';

import { useTranslation } from 'next-i18next';
import { LinkStyled } from '@components/common';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import { useSwrJobInterviewQuestions } from '@hooks/useSwrApi/endpoints/job/useSwrJobInterviewQuestions';
import { CardJobInterview } from '@components/templates/job/Card/CardJobInterview';

/**
 * @description MyInterviewsSection component - displays pending job interviews
 */
export const MyInterviewsSection = ({ locale }: { locale: Locale }) => {
  const { t } = useTranslation('common');
  const { isAuth } = useAuthClient();

  // Fetch pending job interviews
  const { data: pendingInterviews } = useSwrJobInterviewQuestions({
    locale,
    payload: {
      questionStatus: 'unanswered',
    },
  });

  // Only show section if user is authenticated and has pending interviews
  if (!isAuth || !pendingInterviews?.items || pendingInterviews.items.length === 0) {
    return null;
  }

  // Show up to 3 job cards (plus 1 reserve for future use = 4 total)
  const displayItems = pendingInterviews.items.slice(0, 3);

  return (
    <div>
      {/* Section title + View all link */}
      <div className="mb-4 flex items-center justify-between gap-2">
        <h2 className="text-xl font-bold text-ink">
          {t('my-interviews.title')}
        </h2>
        <LinkStyled
          styling="text"
          color="secondary"
          className="shrink-0"
          href="/applied-jobs"
        >
          {t('global.label.see.all.interviews')}
        </LinkStyled>
      </div>

      {/* Job cards grid */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-6">
        {displayItems.map((jobInterview) => (
          <CardJobInterview
            key={jobInterview.jobSlug}
            jobInterview={jobInterview}
            locale={locale}
          />
        ))}
      </div>
    </div>
  );
};

